import { z } from 'zod';

/**
 * A multi-select search param as the URL spells it: a bare value (what every link written before
 * multi-select carried, and what a single selection still writes) or an array. Deliberately no
 * transform to one shape here — the router writes a schema's *output* back to the URL on every
 * navigation, so normalising on parse would turn `?loop=4` into `?loop=[4]`. `selectedValues`
 * normalises at the read site instead.
 */
export const listSearchParam = <T extends z.ZodTypeAny>(item: T) =>
  z
    .union([z.array(item), item])
    .optional()
    .catch(undefined);

const EMPTY: readonly never[] = [];

/** A multi-select param's selections as a list, `[]` when absent. */
export const selectedValues = <T>(value: T | readonly T[] | undefined): readonly T[] => {
  if (value === undefined) return EMPTY;
  return Array.isArray(value) ? value : [value as T];
};

/** The inverse: nothing writes as absent, one selection as the bare value, more as the array. */
export const toListParam = <T>(values: readonly T[]): T | T[] | undefined => {
  if (values.length === 0) return undefined;
  const [first] = values;
  return values.length === 1 && first !== undefined ? first : [...values];
};
